import React, { useEffect } from 'react'
import { Routes, Route, useLocation, Navigate } from 'react-router-dom'
import Home from './pages/Home'
import About from './pages/About'
import Contact from './pages/Contact'
import Property from './pages/Property'
import Rent from './pages/Rent'
import Office from './pages/Office'
import ListingDetail from './pages/ListingDetail'
import AdminLogin from './pages/AdminLogin'
import AdminDashboard from './pages/AdminDashboard'
import AdminCreateListing from './pages/AdminCreateListing'
import AdminRentListings from './pages/AdminRentListings'
import AdminPropertyListings from './pages/AdminPropertyListings'
import AdminSettings from './pages/AdminSettings'
import Legal from './pages/Legal'
import NotFound from './pages/NotFound'
import AdminLayout from './components/admin/AdminLayout'
import Navbar from './components/Navbar'
import Footer from './components/Footer'

const SITE_NAME = 'Millennium Realty'
const SITE_URL = 'https://www.millenniumrealtysf.com'
const DEFAULT_DESCRIPTION =
  'Millennium Realty provides professional real estate and property management services throughout San Francisco and the Bay Area.'

const pageMetadata = {
  '/': {
    title: 'Millennium Realty | San Francisco Property Management',
    description: DEFAULT_DESCRIPTION,
  },
  '/property': {
    title: 'Property Management | Millennium Realty',
    description: 'Professional residential and commercial property management in San Francisco and the Bay Area.',
  },
  '/rent': {
    title: 'Homes & Properties for Rent | Millennium Realty',
    description: 'Browse available homes and commercial properties for rent or lease across the San Francisco Bay Area.',
  },
  '/property-listing': {
    title: 'Property Listings | Millennium Realty',
    description: 'Explore current Millennium Realty property listings throughout San Francisco and the Bay Area.',
  },
  '/about': {
    title: 'About Us | Millennium Realty',
    description: 'Learn about Millennium Realty and our experience managing residential and commercial Bay Area properties.',
  },
  '/contact': {
    title: 'Contact Us | Millennium Realty',
    description: 'Contact Millennium Realty for professional real estate and property management services in the Bay Area.',
  },
  '/privacy': { title: 'Privacy Policy | Millennium Realty', description: 'Read the Millennium Realty privacy policy.' },
  '/terms': { title: 'Terms & Conditions | Millennium Realty', description: 'Read the Millennium Realty website terms and conditions.' },
  '/refund-policy': { title: 'Refund Policy | Millennium Realty', description: 'Read the Millennium Realty refund policy.' },
}

const pageHeadings = {
  '/property': 'Property Management',
  '/rent': 'For Rent / Lease',
  '/property-listing': 'Property Listing',
  '/rent/:slug': 'Rental Listing Details',
  '/property-listing/:slug': 'Property Listing Details',
  '/about': 'About Us',
  '/contact': 'Contact Us',
  '/privacy': 'Privacy Policy',
  '/terms': 'Terms & Conditions',
  '/refund-policy': 'Refund Policy',
}

const setMetaContent = (selector, content) => {
  const element = document.querySelector(selector)
  if (element) element.setAttribute('content', content)
}

const App = () => {
  const location = useLocation()
  const isHome = location.pathname === '/'
  const isAdminRoute = location.pathname.startsWith('/admin')
  const isDetailRoute = /^\/(rent|property-listing)\/[^/]+$/.test(location.pathname)
  const pageTitle =
    pageHeadings[location.pathname] ||
    (location.pathname.startsWith('/rent/') ? pageHeadings['/rent/:slug'] : null) ||
    (location.pathname.startsWith('/property-listing/') ? pageHeadings['/property-listing/:slug'] : null)

  useEffect(() => {
    const isKnownRoute = Boolean(pageMetadata[location.pathname] || isDetailRoute || isAdminRoute)
    const metadata = isAdminRoute
      ? { title: `Admin | ${SITE_NAME}`, description: 'Millennium Realty administration.' }
      : pageMetadata[location.pathname] ||
        (isDetailRoute
          ? { title: `Listing Details | ${SITE_NAME}`, description: 'View property listing details from Millennium Realty.' }
          : { title: `Page Not Found | ${SITE_NAME}`, description: DEFAULT_DESCRIPTION })
    const canonicalUrl = `${SITE_URL}${location.pathname === '/' ? '/' : location.pathname}`

    document.title = metadata.title
    setMetaContent('meta[name="description"]', metadata.description)
    setMetaContent('meta[name="robots"]', isAdminRoute || !isKnownRoute ? 'noindex, nofollow' : 'index, follow')
    setMetaContent('meta[property="og:title"]', metadata.title)
    setMetaContent('meta[property="og:description"]', metadata.description)
    setMetaContent('meta[property="og:url"]', canonicalUrl)
    setMetaContent('meta[name="twitter:title"]', metadata.title)
    setMetaContent('meta[name="twitter:description"]', metadata.description)

    const canonical = document.querySelector('link[rel="canonical"]')
    if (canonical) canonical.setAttribute('href', canonicalUrl)
  }, [location.pathname, isAdminRoute, isDetailRoute])

  useEffect(() => {
    if (location.hash) {
      window.setTimeout(() => document.querySelector(location.hash)?.scrollIntoView({ behavior: 'smooth' }), 0)
    } else {
      window.scrollTo(0, 0)
    }
  }, [location.pathname, location.hash])

  if (isAdminRoute) {
    return (
      <Routes>
        <Route path="/admin/login" element={<AdminLogin />} />
        <Route path="/admin" element={<AdminLayout />}>
          <Route index element={<AdminDashboard />} />
          <Route path="create" element={<AdminCreateListing />} />
          <Route path="rent" element={<AdminRentListings />} />
          <Route path="property-listing" element={<AdminPropertyListings />} />
          <Route path="settings" element={<AdminSettings />} />
          <Route path="*" element={<Navigate to="/admin" replace />} />
        </Route>
      </Routes>
    )
  }

  return (
    <>
      <Navbar/>
      {!isHome && !isDetailRoute && (
        <div className="w-full bg-[#111] border-b border-[#262626] px-[1.5rem] py-[1.15rem] text-center">
          <h1 className="text-white text-[24px] sm:text-[30px] font-bold tracking-[0.03em]">
            {pageTitle ?? 'Millennium Realty'}
          </h1>
        </div>
      )}
      <Routes>
        <Route path='/' element={<Home/>}/>
        <Route path='/about' element={<About/>}/>
        <Route path='/contact' element={<Contact/>} />
        <Route path='/privacy' element={<Legal type="privacy" />} />
        <Route path='/terms' element={<Legal type="terms" />} />
        <Route path='/refund-policy' element={<Legal type="refund" />} />
        <Route path='/property' element={<Property/>}/>
        <Route path='/rent' element={<Rent/>}/>
        <Route path='/property-listing' element={<Office/>}/>
        <Route path='/rent/:slug' element={<ListingDetail listingType="rent" backTo="/rent" />} />
        <Route path='/property-listing/:slug' element={<ListingDetail listingType="property" backTo="/property-listing" />} />
        <Route path='*' element={<NotFound />} />
      </Routes>
      <Footer/>
    </>
  )
}

export default App
